import React, { useCallback, useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Typography,
} from "@mui/material";
import { Formik, Form, FieldArray, FieldInputProps } from "formik";
import * as Yup from "yup";
import Loader from "../../Common/assets/Loader";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { commonContainerWrapper } from "../../Common/styles/flex";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import { Add, Delete } from "@mui/icons-material";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { Languages } from "../../../Redux/reducers/studySlice";
import { landingPageImageUploadUrl } from "../../../utils/upload";
import { useDropzone } from "react-dropzone";

type Props = {
  languages: Languages[];
  studyId?: string;
  canEdit: boolean;
};

type FormInputProps = {
  label: string;
  isOptional?: boolean;
  getFieldProps: FieldInputProps<any>;
  multiline?: boolean;
  errors: any;
  touched: any;
  meta: string;
  disabled: boolean;
};

const uploadWrapper: SxProps = {
  width: 130,
  height: 90,
  borderRadius: "8px",
  border: 2,
  borderColor: "primary.main",
  borderStyle: "dashed",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  py: 2,
  px: 4,
  "&:hover": {
    bgcolor: "secondary.dark",
    cursor: "pointer",
  },
};

const UploadImageItem: React.FC<any> = ({
  image,
  setFieldValue,
  readOnly,
  name,
  fieldName,
  studyId,
}) => {
  const [loading, setLoading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: any) => {
      try {
        const file = acceptedFiles?.[0];
        if (file) {
          if (file.size > 10 * 1024 * 1024) {
            toastMessage("warning", "File Size cannot be greater than 10 MB!");
            return;
          }
          setLoading(true);
          const url = await landingPageImageUploadUrl(file, studyId);
          if (url) {
            const obj = {
              previewUrl: url?.previewUrl,
              url: url?.postUploadImageUrl,
            };
            setFieldValue(fieldName, obj);
          }
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    },
    [setFieldValue, fieldName, studyId]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: !readOnly ? onDrop : undefined,
    multiple: false,
    accept: {
      "image/jpg": [],
      "image/jpeg": [],
      "image/png": [],
    },
    noClick: readOnly,
  });

  return (
    <>
      {loading && (
        <Backdrop
          open={true}
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <Loader />
        </Backdrop>
      )}
      <Box
        {...getRootProps({ className: "dropzone" })}
        sx={{
          ...uploadWrapper,
        }}
      >
        <input {...getInputProps()} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& .preview-img": {
              width: "130px",
              height: "90px",
              borderRadius: "8px",
            },
          }}
        >
          {image ? (
            <img src={image} className="preview-img" alt={name} />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Add fontSize="medium" sx={{ color: "text.secondary" }} />
              <Typography
                sx={{ color: "text.secondary" }}
                variant="body2"
                fontWeight={"medium"}
                textAlign={"center"}
              >
                Upload image
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Typography variant="body1" color={"text.secondary"} mt={2} width={"75%"}>
        Image size - 258x70px, 10MB Max
      </Typography>
    </>
  );
};

const FormInput = ({
  label,
  isOptional = false,
  getFieldProps,
  multiline = false,
  errors,
  touched,
  meta,
  disabled = false,
}: FormInputProps) => {
  return (
    <FormControl sx={InputWrapper}>
      <FormLabel sx={LabelStyle}>
        {label} {!isOptional && <span style={{ color: "red" }}>*</span>}
      </FormLabel>
      <TextField
        {...getFieldProps}
        placeholder={label}
        multiline={multiline}
        fullWidth
        error={touched?.hero?.[meta] && errors?.hero?.[meta] ? true : false}
        helperText={
          touched?.hero?.[meta] && errors?.hero?.[meta]
            ? errors?.hero?.[meta]
            : " "
        }
        InputProps={{
          readOnly: disabled,
        }}
      />
    </FormControl>
  );
};

const validationSchema = Yup.object().shape({
  hero: Yup.object().shape({
    title: Yup.string().required("Title is required"),
    subtitle: Yup.string().required("Subtitle is required"),
    description: Yup.string().required("Description is required"),
  }),
  eligibility: Yup.array()
    .of(Yup.string().required("Required"))
    .min(1, "At least one eligibility criterion is required"),
  benefits: Yup.array()
    .of(Yup.string().required("Required"))
    .min(1, "At least one benefit is required"),
  faq: Yup.array()
    .of(
      Yup.object().shape({
        question: Yup.string().required("Question is required"),
        answer: Yup.string().required("Answer is required"),
      })
    )
    .min(1, "At least one FAQ is required"),
  headerLogo: Yup.object().shape({
    url: Yup.string().optional().nullable(),
    previewUrl: Yup.string().optional().nullable(),
    key: Yup.string().optional().nullable(),
  }),
});

const LandingPageBuilder = ({ languages, studyId, canEdit }: Props) => {
  const [loading, setLoading] = useState(true);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [toggleLoader, setToggleLoader] = useState(false);

  const [lang, setLang] = useState<string>("en");
  const [data, setData] = useState<any>(null);
  const [images, setImages] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let res: AxiosResponse;
        if (studyId) {
          res = await http.get(
            `/ui/pages?studyId=${studyId}&pageKey=landing_page&lang=${lang}`
          );
        } else {
          res = await http.get(
            `/admin/registry/ui/pages?pageKey=landing_page&lang=${lang}`
          );
        }
        const data = res?.data?.data;
        setData(data?.content || null);
        const imgs = data?.images?.filter((img: any) => img?.key === "header");
        setImages(imgs || []);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchData();
  }, [studyId, toggleLoader, lang]);

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const body: any = {
        layoutKey: "layout_1",
        pageKey: "landing_page",
        content: values,
        lang,
        images: values?.headerLogo?.url
          ? [
              {
                url:
                  values.headerLogo.postUploadImageUrl ?? values.headerLogo.url,
                key: "header",
              },
            ]
          : [],
      };
      if (studyId) {
        body.studyId = studyId;
      }
      let res: AxiosResponse;
      if (studyId) {
        res = await http.post(`/ui/pages`, body);
      } else {
        res = await http.post(`/admin/registry/ui/pages`, body);
      }

      toastMessage("success", res.data.message);
      setSubmitLoader(false);
      setToggleLoader((prev) => !prev);
    } catch (err) {
      setSubmitLoader(false);
      errorToastMessage(err as Error);
    }
  };

  return (
    <>
      {loading && (
        <Backdrop
          open={true}
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <Loader />
        </Backdrop>
      )}

      {!loading && (
        <Formik
          initialValues={{
            hero: data?.hero || {
              title: "",
              subtitle: "",
              description: "",
            },
            eligibility: data?.eligibility || [""],
            benefits: data?.benefits || [""],
            faq: data?.faq || [{ question: "", answer: "" }],
            headerLogo:
              images?.length > 0
                ? images?.[0]
                : {
                    url: "",
                    key: "header",
                    previewUrl: "",
                  },
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched, setFieldValue, values }) => (
            <Form>
              <Box sx={commonContainerWrapper}>
                {/* HERO */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h3" mb={2}>
                    Primary Introduction
                  </Typography>
                  {languages.length > 1 && (
                    <Select
                      value={lang}
                      onChange={(e) => {
                        setLang(e.target.value);
                      }}
                      id="lang"
                      sx={{ height: "48px" }}
                    >
                      {languages.map((lang) => (
                        <MenuItem
                          key={lang.languageCode}
                          value={lang.languageCode}
                        >
                          {lang.languageLabel}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </Box>
                <Box
                  sx={{
                    width: "50%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <FormInput
                    getFieldProps={getFieldProps("hero.title")}
                    label="Title"
                    errors={errors}
                    touched={touched}
                    meta="title"
                    disabled={!canEdit}
                  />
                  <FormInput
                    getFieldProps={getFieldProps("hero.subtitle")}
                    label="Subtitle"
                    errors={errors}
                    touched={touched}
                    meta="subtitle"
                    disabled={!canEdit}
                  />
                  <FormInput
                    getFieldProps={getFieldProps("hero.description")}
                    label="Description"
                    multiline
                    errors={errors}
                    touched={touched}
                    meta="description"
                    disabled={!canEdit}
                  />
                  <FormControl>
                    <Box>
                      <FormLabel sx={LabelStyle} htmlFor="logo">
                        Images
                      </FormLabel>
                      <IconButton
                        disabled={!values?.headerLogo?.url}
                        color="error"
                        onClick={() =>
                          setFieldValue("headerLogo", {
                            url: "",
                            key: "header",
                            previewUrl: "",
                          })
                        }
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                    <UploadImageItem
                      image={values?.headerLogo?.previewUrl}
                      name={"Header"}
                      setFieldValue={setFieldValue}
                      readOnly={!canEdit}
                      fieldName={"headerLogo"}
                      studyId={studyId}
                    />
                    <FormHelperText>
                      {touched?.headerLogo && errors?.headerLogo
                        ? (errors?.headerLogo as string)
                        : " "}
                    </FormHelperText>
                  </FormControl>
                </Box>

                {/* ELIGIBILITY */}
                <Typography variant="h3" mt={4} mb={2}>
                  Eligibility
                </Typography>
                <FieldArray name="eligibility">
                  {({ push, remove, form }) => (
                    <Box
                      sx={{
                        width: "50%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {form.values.eligibility.map(
                        (_: string, index: number) => (
                          <Box
                            key={index}
                            display="flex"
                            gap={2}
                            sx={{ alignItems: "center" }}
                          >
                            <TextField
                              fullWidth
                              placeholder={`Eligibility ${index + 1}`}
                              {...getFieldProps(`eligibility[${index}]`)}
                              error={
                                (touched?.eligibility as any)?.[index] &&
                                (errors.eligibility as any)?.[index]
                                  ? true
                                  : false
                              }
                              helperText={
                                (touched?.eligibility as any)?.[index] &&
                                (errors.eligibility as any)?.[index]
                                  ? ((errors.eligibility as any)[
                                      index
                                    ] as string)
                                  : " "
                              }
                              InputProps={{
                                readOnly: !canEdit,
                              }}
                            />
                            <IconButton
                              onClick={() => remove(index)}
                              disabled={
                                form.values.eligibility.length === 1 || !canEdit
                              }
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        )
                      )}
                      <Box>
                        <Button
                          onClick={() => push("")}
                          variant="outlined"
                          disabled={!canEdit}
                        >
                          Add Eligibility
                        </Button>
                      </Box>
                    </Box>
                  )}
                </FieldArray>

                {/* BENEFITS */}
                <Typography variant="h3" mt={4} mb={2}>
                  Benefits
                </Typography>
                <FieldArray name="benefits">
                  {({ push, remove, form }) => (
                    <Box
                      sx={{
                        width: "50%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {form.values.benefits.map((_: string, index: number) => (
                        <Box
                          key={index}
                          display="flex"
                          gap={2}
                          sx={{ alignItems: "center" }}
                        >
                          <TextField
                            fullWidth
                            placeholder={`Benefit ${index + 1}`}
                            {...getFieldProps(`benefits[${index}]`)}
                            error={
                              (touched?.benefits as any)?.[index] &&
                              (errors.benefits as any)?.[index]
                                ? true
                                : false
                            }
                            helperText={
                              (touched?.benefits as any)?.[index] &&
                              (errors.benefits as any)?.[index]
                                ? ((errors.benefits as any)[index] as string)
                                : " "
                            }
                            InputProps={{
                              readOnly: !canEdit,
                            }}
                          />
                          <IconButton
                            onClick={() => remove(index)}
                            disabled={
                              form.values.benefits.length === 1 || !canEdit
                            }
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      ))}
                      <Box>
                        <Button
                          onClick={() => push("")}
                          variant="outlined"
                          disabled={!canEdit}
                        >
                          Add Benefit
                        </Button>
                      </Box>
                    </Box>
                  )}
                </FieldArray>

                {/* FAQ */}
                <Typography variant="h3" mt={4} mb={2}>
                  FAQ
                </Typography>
                <FieldArray name="faq">
                  {({ push, remove, form }) => (
                    <Box
                      sx={{
                        width: "70%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {form.values.faq.map((_: any, index: number) => (
                        <Box
                          key={index}
                          display="flex"
                          flexDirection="column"
                          gap={1}
                        >
                          <TextField
                            fullWidth
                            placeholder={`FAQ Question ${index + 1}`}
                            {...getFieldProps(`faq[${index}].question`)}
                            error={
                              (touched.faq as any)?.[index]?.question &&
                              (errors.faq as any)?.[index]?.question
                                ? true
                                : false
                            }
                            helperText={
                              (touched.faq as any)?.[index]?.question &&
                              (errors.faq as any)?.[index]?.question
                                ? (errors.faq as any)[index].question
                                : " "
                            }
                            InputProps={{
                              readOnly: !canEdit,
                            }}
                          />

                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              alignItems: "flex-end",
                            }}
                          >
                            <TextField
                              fullWidth
                              placeholder="Answer"
                              multiline
                              rows={3}
                              {...getFieldProps(`faq[${index}].answer`)}
                              error={
                                (touched.faq as any)?.[index]?.answer &&
                                (errors.faq as any)?.[index]?.answer
                                  ? true
                                  : false
                              }
                              helperText={
                                (touched.faq as any)?.[index]?.answer &&
                                (errors.faq as any)?.[index]?.answer
                                  ? (errors.faq as any)[index].answer
                                  : " "
                              }
                              InputProps={{
                                readOnly: !canEdit,
                              }}
                            />

                            <Box sx={{ mb: 2 }}>
                              <IconButton
                                onClick={() => remove(index)}
                                disabled={
                                  form.values.faq.length === 1 || !canEdit
                                }
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                      <Box>
                        <Button
                          onClick={() => push({ question: "", answer: "" })}
                          variant="outlined"
                          disabled={!canEdit}
                        >
                          Add FAQ
                        </Button>
                      </Box>
                    </Box>
                  )}
                </FieldArray>

                {canEdit && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 4,
                    }}
                  >
                    {submitLoader ? (
                      <CircularProgress size={25} />
                    ) : (
                      <Button type="submit" variant="contained">
                        Save
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default LandingPageBuilder;
